'use client';

import { useCallback, useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import { useUnit } from 'effector-react';
import { Paywall } from '@nx-frontend/features/paywall';
import { $ModalStore, closeModalEvent } from '@nx-frontend/shared/ui/modal';
import { trackLimitBlocksPopup, trackLimitNotesPopup } from '@nx-frontend/shared/analytics';
import { USER_VARIABLE } from '@nx-frontend/shared/api';
import { clientSendWorkspaceEvent } from '@nx-frontend/shared/api/webnotes/client';
import { generalEnv } from '@nx-frontend/shared/env';
import { useDebounce } from '@nx-frontend/shared/hooks';
import { isFromCallPopupEnabled } from '@nx-frontend/shared/popups/from-call-popup';
import { $app } from '@nx-frontend/shared/store/app';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { $orgCoupons } from '@nx-frontend/shared/store/org-coupons';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { $portalSettings } from '@nx-frontend/shared/store/portal';
import { $app as $customizerApp } from '@nx-frontend/shared/store/portal-app';
import { $session } from '@nx-frontend/shared/store/session';
import { $user } from '@nx-frontend/shared/store/user';
import { $userVars, setUserVarEvent } from '@nx-frontend/shared/store/user-vars';
import { PaywallPopupPayload, PopupType, PaywallPopupReason } from '@nx-frontend/shared/types';
import {
  GetOpenFromCallPopupProps,
  GetOpenFromCallEndPopupProps,
  GetOpenPayWallPopupProps,
} from '@nx-frontend/shared/types';
import { PaywallMode } from '@nx-frontend/shared/types/paywall-types';
import { getSubscriptionType, hasMaxSubscriptionPlan } from '@nx-frontend/shared/utils';
import { hasAppsumoBundle, onUserPilotLoad } from '@nx-frontend/shared/utils';

export { hasMaxSubscriptionPlan };

interface UserPilotStepEvent {
  id: number;
  name: string;
  step: number;
  step_id: string;
  token: string;
  totalSteps: number;
}

const modalContainerStyles = 'bg-transparent';

const blackFriday = {
  startDate: 1720087200, // 04.07.2024 12:00 Kiev
  endDate: 1720810800, // 12.07.2024 12:00 San Francisco (21:00 Kiev)
  discount: 35, //%
  coupon: 'id2024',
};

const redirectToCustomizer = false;

const localStorageDocumentLeaveKey = 'paywallDocumentLeave';

/**
 * Interval ID for checking userpilot frame shown
 */
let exitPopupIID: NodeJS.Timeout | null = null;
/**
 * Try count for checking userpilot frame shown
 * Need to prevent infinity loop
 */
let exitPopupCheckTryCount = 50;
/**
 * Userpilot trigger id for exit popup
 */
const USERPILOT_EXIT_POPUP_TRIGGER_ID = '0Mzt8ZC1Kf';

/**
 * Event handler for mouseleave event on document
 * Show userpilot exit popup and send event workspaces:exitPaywallPopup
 */
let documentLeaveTID: NodeJS.Timeout | null = null;
const onDocumentLeave = () => {
  documentLeaveTID && clearTimeout(documentLeaveTID);
  documentLeaveTID = setTimeout(() => {
    if (!localStorage.getItem(localStorageDocumentLeaveKey)) {
      try {
        if (!document.querySelector('#userpilotContent .userpilot-backdrop')) {
          onUserPilotLoad(() => {
            window['userpilot']?.trigger(USERPILOT_EXIT_POPUP_TRIGGER_ID);
            exitPopupIID && clearInterval(exitPopupIID);
            exitPopupIID = setInterval(() => {
              exitPopupCheckTryCount--;
              if (exitPopupCheckTryCount <= 0) {
                exitPopupIID && clearInterval(exitPopupIID);
              }
              // check is userpilot frame shown
              if (document.querySelector('#userpilotIframeContainer')) {
                exitPopupIID && clearInterval(exitPopupIID);
                localStorage.setItem(localStorageDocumentLeaveKey, 'true');
                clientSendWorkspaceEvent({
                  name: 'workspaces:exitPaywallPopup',
                  payload: {},
                });
              }
            }, 500);
          });
        }
      } catch (e) {
        console.error('Error tracking paywall_leave event', e);
      }
    }
  }, 1000);
};
/**
 * On document mouse enter remove timeout for mouseleave event, to prevent show exit popup
 */
const onDocumentEnter = () => {
  documentLeaveTID && clearTimeout(documentLeaveTID);
};

/**
 * @deprecated Use `useOpenPaywallPopupNew`
 */
export const getOpenPaywallPopup = ({ openModal, payload }: GetOpenPayWallPopupProps) => {
  let useFromCallUpgrade = false;
  if (typeof window !== 'undefined') {
    if ('on' === window.localStorage.getItem('useFromCallUpgrade')) {
      useFromCallUpgrade = isFromCallPopupEnabled;
    }
  }
  if (useFromCallUpgrade) {
    openModal({
      showCloseButton: false,
      content: PopupType.FROM_CALL,
      modalContainerStyles: 'bg-[#011519] !rounded-[12px]',
      modalGradient: 'linear-gradient(90deg, #001418 4.38%, #002028 59.57%, #001418 99.95%), #011519',
      usePadding: false,
      payload,
      multipopup: false,
    });
  } else {
    openModal({
      content: PopupType.PAYWALL,
      showCloseButton: false,
      isFullScreen: true,
      modalContainerStyles,
      payload,
      multipopup: false,
      ...(payload && 'escapeToClose' in payload ? { escapeToClose: payload.escapeToClose } : {}),
    });
    if (payload?.reason) {
      switch (payload.reason) {
        case PaywallPopupReason.NOTES_LIMIT:
          trackLimitNotesPopup();
          break;
        case PaywallPopupReason.BLOCKS_LIMIT:
          trackLimitBlocksPopup();
          break;
      }
    }
  }

  // trigger global event for paywall popup
  const event = new CustomEvent('paywallPopupOpened', {
    detail: {
      reason: payload?.reason,
      mode: payload?.mode,
    },
  });
  document.dispatchEvent(event);
};

export const getOpenFromCallPopup = ({ openModal, payload }: GetOpenFromCallPopupProps) => {
  openModal({
    showCloseButton: false,
    content: PopupType.FROM_CALL,
    modalContainerStyles: 'bg-[#011519] !rounded-[12px]',
    modalGradient: 'linear-gradient(90deg, #001418 4.38%, #002028 59.57%, #001418 99.95%), #011519',
    usePadding: false,
    payload,
  });
};

export const getOpenFromCallEndPopup = ({ openModal, payload }: GetOpenFromCallEndPopupProps) => {
  openModal({
    showCloseButton: false,
    content: PopupType.FROM_CALL_END,
    modalContainerStyles: 'bg-[#011519] !rounded-[12px]',
    modalGradient: 'linear-gradient(90deg, #001418 4.38%, #002028 59.57%, #001418 99.95%), #011519',
    usePadding: false,
    payload,
    escapeToClose: false,
    disabledClose: true,
  });
};

export const goToAppsumoBundlePurchase = (orgId: string) => {
  window.open(generalEnv.purchaseAppsumoBundleUrl.replace('{orgId}', orgId));
};

function getIsFromWizard(enabled: boolean) {
  if (typeof window === 'undefined') {
    return false;
  }
  const url = window.location.href;
  const urlParams = new URLSearchParams(new URL(url).search);
  const fromValue = urlParams.get('from');
  return fromValue === 'wizard' && enabled;
}

export function PaywallPopup() {
  const [
    modalStore,
    app,
    user,
    orgInfo,
    billingInfo,
    currentWorkspace,
    currentOrganization,
    portalSettings,
    orgCoupons,
    customizerApp,
    userVars,
    session,
  ] = useUnit([
    $ModalStore,
    $app,
    $user,
    $orgInfo,
    $billingInfo,
    $currentWorkspace,
    $currentOrganization,
    $portalSettings,
    $orgCoupons,
    $customizerApp,
    $userVars,
    $session,
  ]);

  const [closeModal, setUserVar] = useUnit([closeModalEvent, setUserVarEvent]);
  const subscriptionType = getSubscriptionType({ orgInfo, orgCoupons, billingInfo });
  const portalId = portalSettings.id;
  const t = useTranslations('features');
  const pathname = usePathname();

  const clientUrl = useMemo(() => {
    const [, , , pageType] = pathname.split('/');
    let url = `/dashboard/${currentWorkspace.orgId}/space/${currentWorkspace.globalId}`;
    if (pageType === 'agents') {
      url = `/space/${currentWorkspace.globalId}/agents`;
    }
    return `${url}`;
  }, [pathname, currentWorkspace.orgId, currentWorkspace.globalId]);

  let mode = 'upgrade';
  let title = t('paywall.title_upgrade_now');
  let reason = PaywallPopupReason.BOTTOM_MENU_UPGRADE;
  let coupon: string | undefined;

  const isAppsumoBundle = hasAppsumoBundle(billingInfo, orgCoupons);
  const hasAppsumoCoupon = Object.keys(orgCoupons.couponNumber).find((coupon) => coupon.indexOf('appsumo') === 0);

  if (modalStore.length > 0) {
    const modal = modalStore.find((m) => m.content === PopupType.PAYWALL);
    if (modal && modal.payload && modal.payload.payloadType === PopupType.PAYWALL) {
      const payload: PaywallPopupPayload = modal.payload;

      if (payload.mode) {
        mode = payload.mode;
      }
      if (payload.title) {
        title = payload.title;
      }
      if (payload.reason) {
        reason = payload.reason;
      }
      if (payload.coupon) {
        coupon = payload.coupon;
      }
    }
  }

  const customizerUrl = `portal/${portalId}/visual?preview=true&firstSession=true`;

  const paywallAdditionPaymentParams: Record<string, string> = {};
  // collect paywallAdditionPaymentParams for new paywall
  if (coupon) {
    paywallAdditionPaymentParams['coupon'] = coupon;
  }
  if (
    Date.now() >= blackFriday.startDate * 1000 &&
    blackFriday.endDate * 1000 >= Date.now() &&
    !hasAppsumoCoupon &&
    ['trialStart', 'upgrade'].includes(mode)
  ) {
    paywallAdditionPaymentParams['coupon'] = blackFriday.coupon;
  }
  if (reason === PaywallPopupReason.TRANSCRIBE) {
    paywallAdditionPaymentParams['transcribe-addon'] = '1';
  }
  if (reason === PaywallPopupReason.AI_LIMIT) {
    paywallAdditionPaymentParams['ai'] = '1';
  }
  if (reason === PaywallPopupReason.AUTOMATION) {
    paywallAdditionPaymentParams['automation-addon'] = '1';
  }
  if (mode === 'trialStart') {
    paywallAdditionPaymentParams['redirect_url'] = getIsFromWizard(redirectToCustomizer)
      ? `${encodeURIComponent(customizerUrl)}`
      : `${encodeURIComponent(clientUrl)}`;
  }
  // end collect paywallAdditionPaymentParams for new paywall

  let nimOrgId = currentWorkspace ? currentWorkspace.orgId : null;
  if (!nimOrgId && currentOrganization) {
    nimOrgId = currentOrganization.globalId;
  }

  if (!nimOrgId) {
    console.error(`Empty nimOrgId`);
  }

  // const { showFreeTrial } = useMemo(() => {
  //   let show = typeof window === 'undefined' ? false : window.location.search.includes('freetrial=1');
  //   if (user && user.remoteApplication) {
  //     if (['screens_chrome', 'screens_ff'].includes(user.remoteApplication)) {
  //       show = true;
  //     }
  //   }
  //   return {
  //     showFreeTrial: show,
  //   };
  // }, [user]);

  const showFreeTrial = true; // Show free trial for all users

  /**
   * Track userpilot paywall event
   * Subscribe to mouseleave event to track paywall_leave event
   */
  const trackUserpilot = useCallback((nimMode: string) => {
    if (['trialStart'].includes(nimMode)) {
      onUserPilotLoad(() => {
        try {
          window['userpilot']?.track('paywall');
        } catch (e) {
          console.error('Error tracking paywall event', e);
        }
      });
      document.addEventListener('mouseleave', onDocumentLeave);
    }
  }, []);

  useEffect(() => {
    trackUserpilot(mode);
    document.addEventListener('mouseenter', onDocumentEnter);
    return () => {
      document.removeEventListener('mouseleave', onDocumentLeave);
      document.addEventListener('mouseenter', onDocumentEnter);
    };
  }, [mode, trackUserpilot]);

  /**
   * Hide userpilot Academy popup
   */
  useEffect(() => {
    const stylesElId = 'paywall-userpilot-styles';
    const onUserPilotStep = (step: UserPilotStepEvent) => {
      if (step.token === 'othKPJe1iJ') {
        // add styles to hide userpilot popup
        if (!document.getElementById(stylesElId)) {
          const style = document.createElement('style');
          style.setAttribute('id', stylesElId);
          style.innerHTML = `
              iframe#userpilotIframeContainer {
                inset: -99999px !important;
              }
            `;
          document.body.appendChild(style);
        }
      } else {
        // if show other userpilot popup, remove styles
        document.getElementById(stylesElId)?.remove();
      }
    };
    onUserPilotLoad(() => {
      window['userpilot']?.on<UserPilotStepEvent>('step', onUserPilotStep);
    });
    return () => {
      document.getElementById(stylesElId)?.remove();
      window['userpilot']?.off('step');
    };
  }, []);

  /**
   * When user is see trial start popup first time, send event and write value to userVars
   * https://nimbusweb.atlassian.net/browse/NIM-36039
   */
  const onTrialStartDebounced = useDebounce<void>(
    useCallback(() => {
      if (mode === 'trialStart') {
        if (userVars.find((userVar) => userVar.key === USER_VARIABLE.SHOW_TRIAL_START_POPUP)?.value) {
          return;
        }
        clientSendWorkspaceEvent({
          name: 'workspaces:showTrialStartPopup',
          payload: {},
        });
        setUserVar({
          key: USER_VARIABLE.SHOW_TRIAL_START_POPUP,
          value: true,
        });
      }
    }, [mode, userVars, setUserVar]),
    100
  );

  useEffect(() => {
    onTrialStartDebounced();
  }, [onTrialStartDebounced]);

  return (
    <div data-cy={`paywall-popup-${reason}`} className="w-full min-h-full flex justify-center items-center">
      <Paywall
        mode={mode as PaywallMode}
        title={title}
        onClose={closeModal}
        paywallAdditionPaymentParams={paywallAdditionPaymentParams}
        reason={reason}
        isAppsumo={isAppsumoBundle}
        showFreeTrial={showFreeTrial}
        subscriptionType={subscriptionType}
        settings={{
          gatewayUrl: generalEnv.gatewayUrl,
          userBillingUrl: generalEnv.userBillingUrl,
          sessionId: app.sessionId || customizerApp.sessionId || session.id || '',
          userId: user.id,
          orgId: currentOrganization.globalId,
        }}
      />
    </div>
  );
}
